import React, { useEffect, useMemo, useState } from "react";
import { useCart } from "../../contexts/CartContext";
import type { Product } from "../../types/store";

type ControlTab = "customer" | "admin";

type CatalogProduct = Product & {
    badge: string;
    stock: string;
    accent: string;
};

const baseImage =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='360' height='480'%3E%3Crect width='100%25' height='100%25' rx='36' ry='36' fill='%23101010'/%3E%3Cpath d='M40 60h280v4H40z' fill='%23242424'/%3E%3Ccircle cx='90' cy='240' r='42' fill='none' stroke='%23ffffff22' stroke-width='6'/%3E%3Ccircle cx='270' cy='180' r='32' fill='none' stroke='%23ffffff15' stroke-width='5'/%3E%3C/svg%3E";

const catalogProducts: CatalogProduct[] = [
    {
        id: "texture-atlas",
        name: "Texture Lab Atlas",
        type: "TEXTURE",
        thumbnailUrl: baseImage,
        description: "40+ ACES-ready surfaces & USDZ presets.",
        price: 48,
        tags: ["Instant download", "4K"],
        badge: "Digital",
        stock: "Sınırsız erişim",
        accent: "from-[#7b5bff]/20 via-transparent to-transparent"
    },
    {
        id: "figur-kit",
        name: "Figur Lab Resin Kit",
        type: "PHYSICAL",
        thumbnailUrl: baseImage,
        description: "Numaralı sertifika + premium paketleme.",
        price: 210,
        tags: ["Drop", "Hand finish"],
        badge: "Drop",
        stock: "12 adet kaldı",
        accent: "from-[#f6c096]/30 via-transparent to-transparent"
    },
    {
        id: "blueprint-course",
        name: "Hard Surface Blueprint",
        type: "TEXTURE",
        thumbnailUrl: baseImage,
        description: "Pipeline breakdown videoları ve testler.",
        price: 89,
        tags: ["Education", "Certificate"],
        badge: "Education",
        stock: "Anında erişim",
        accent: "from-[#6be6c8]/25 via-transparent to-transparent"
    },
    {
        id: "shader-stack",
        name: "Figur Lab Skin Shader",
        type: "TEXTURE",
        thumbnailUrl: baseImage,
        description: "UE5 / Blender için subsurface presetleri.",
        price: 52,
        tags: ["Shader", "Metahuman"],
        badge: "Toolkit",
        stock: "Download ready",
        accent: "from-[#ff6bb4]/20 via-transparent to-transparent"
    }
];

const adminStats = [
    { label: "Bugünkü gelir", value: "$4,920", delta: "+18%" },
    { label: "Aktif üyeler", value: "312", delta: "+42 yeni" },
    { label: "Drop stoğu", value: "32/40", delta: "%80 dolu" },
    { label: "Ticket SLA", value: "02:18", delta: "hedef altında" }
];

const automationRules = [
    {
        title: "Shopify sync",
        detail: "Ürün & fiyatlar overlay ile eşleniyor.",
        status: "Aktif"
    },
    {
        title: "Kargo etiketi",
        detail: "UPS & DHL etiketleri siparişte tetiklenir.",
        status: "Aktif"
    },
    {
        title: "Figur Lab brief",
        detail: "Tamamlanan form Slack #fabric kanalına düşer.",
        status: "Pasif"
    }
];

const activityLog = [
    { time: "09:42", actor: "Sera", action: "Texture Lab Atlas fiyatını $42 → $48 yaptı" },
    { time: "11:05", actor: "Auto", action: "12 drop stoğu Shopify POS ile eşitlendi" },
    { time: "13:27", actor: "Client", action: "Figur Lab hesabı oluşturdu" }
];

const workflowStages = [
    { label: "Ön sipariş", progress: 68 },
    { label: "Üretim kuyruğu", progress: 44 },
    { label: "Kargoda", progress: 22 },
    { label: "Teslim edildi", progress: 81 }
];

const conciergeHighlights = [
    {
        title: "Üyelik opsiyonel",
        copy: "Giriş paneli sadece alışverişe hazır olduğunuzda açılır."
    },
    {
        title: "Tek ekran yönetişim",
        copy: "Shopify siparişleri, Figur Lab komisyonları ve eğitim satışları aynı akışta."
    },
    {
        title: "Anlık bildirim",
        copy: "Overlay üzerinden yapılan her işlem, yönetim panelinin sağ üstünde belirir."
    }
];

const supportContacts = [
    { label: "Operasyon", handle: "ops@modelerman", status: "Online" },
    { label: "Finans", handle: "billing@modelerman", status: "2 saat" },
    { label: "Servis", handle: "figur.lab", status: "Slack" }
];

const StoreLanding: React.FC = () => {
    const { addItem } = useCart();
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeTab, setActiveTab] = useState<ControlTab>("customer");
    const [primaryProduct] = useState(catalogProducts[0]);

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const premiumCatalog = useMemo(() => catalogProducts.slice(1), []);

    const addToCart = (product: Product) => {
        addItem({ product, license: "PERSONAL" });
    };

    const currentYear = new Date().getFullYear();

    return (
        <div className="min-h-screen bg-[#050307] text-white">
            <div className="relative isolate overflow-hidden">
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,#08050d_0%,#0a0815_45%,#050307_100%)]" />
                <div className="pointer-events-none absolute inset-0 opacity-70">
                    <div className="absolute left-16 top-16 h-72 w-72 rounded-full bg-[#8a6cff]/30 blur-[140px]" />
                    <div className="absolute right-10 bottom-10 h-96 w-96 rounded-full bg-[#f5b88a]/25 blur-[200px]" />
                </div>

                <header
                    className={`sticky top-0 z-40 border-b border-white/5 px-6 transition-all duration-300 ${isScrolled ? "bg-black/80 backdrop-blur" : "bg-black/40 backdrop-blur-sm"}`}
                >
                    <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 py-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-lg font-semibold">M</div>
                            <div>
                                <p className="text-sm font-semibold tracking-[0.4em] text-white">MODELERMAN</p>
                                <p className="text-xs uppercase tracking-[0.4em] text-white/60">Store</p>
                            </div>
                        </div>
                        <nav className="flex flex-1 flex-wrap items-center gap-4 text-sm font-medium text-white/70">
                            {[
                                "Texture Lab",
                                "Figur Lab",
                                "STL Collections",
                                "Physical"
                            ].map((link) => (
                                <a key={link} href="#" className="transition hover:text-white">
                                    {link}
                                </a>
                            ))}
                        </nav>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                            <button className="rounded-full border border-white/25 px-4 py-2 text-white/80 transition hover:border-white/60" type="button">
                                Sign in
                            </button>
                            <button className="rounded-full bg-[#f5a524] px-5 py-2 font-semibold text-black transition hover:brightness-110" type="button">
                                Create account
                            </button>
                            <button className="rounded-full border border-white/25 px-3 py-2 text-white/80 transition hover:border-white/60" type="button">
                                Cart
                            </button>
                            <button className="rounded-full border border-white/25 px-3 py-2 text-white/80 transition hover:border-white/60" type="button">
                                Close
                            </button>
                        </div>
                        <div className="ml-auto flex gap-3 rounded-full border border-white/10 bg-white/5 p-1 text-[0.65rem] uppercase tracking-[0.3em]">
                            <button
                                type="button"
                                onClick={() => setActiveTab("customer")}
                                className={`rounded-full px-4 py-2 transition ${activeTab === "customer" ? "bg-white text-black" : "text-white/70"}`}
                            >
                                Müşteri arayüzü
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab("admin")}
                                className={`rounded-full px-4 py-2 transition ${activeTab === "admin" ? "bg-white text-black" : "text-white/70"}`}
                            >
                                Yönetim paneli
                            </button>
                        </div>
                    </div>
                </header>

                <main className="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-12">
                    <section className="grid gap-10 lg:grid-cols-[1.1fr,0.9fr]">
                        <div className="rounded-[34px] border border-white/10 bg-white/5 p-8 shadow-[0_25px_80px_rgba(0,0,0,0.45)]">
                            <p className="text-[0.65rem] uppercase tracking-[0.6em] text-[#cbb5ff]">Overlay giriş katmanı</p>
                            <h1 className="mt-4 text-4xl font-light leading-tight text-white">
                                Üyelik açmadan ürünleri keşfedin, hazır olduğunuzda paneli kendiniz açın.
                            </h1>
                            <p className="mt-4 max-w-2xl text-sm text-white/70">
                                Customer overlay ekranı; Texture Lab, Figur Lab ve fiziksel drop kartlarını siyah arayüzde yan yana tutar.
                                Sağ tarafta Shopify benzeri kontrol merkezi; siparişleri, stokları ve otomasyonları tek bakışta yönetir.
                            </p>
                            <div className="mt-8 grid gap-6 lg:grid-cols-2">
                                <div className="rounded-3xl border border-white/10 bg-black/60 p-6">
                                    <div className="flex items-center justify-between text-xs uppercase tracking-[0.4em] text-white/50">
                                        <span>Müşteri girişi</span>
                                        <span>Opsiyonel</span>
                                    </div>
                                    <form className="mt-6 space-y-4 text-sm">
                                        <input
                                            className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 placeholder:text-white/40 focus:border-white/60 focus:outline-none"
                                            placeholder="E-posta"
                                            type="email"
                                        />
                                        <input
                                            className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 placeholder:text-white/40 focus:border-white/60 focus:outline-none"
                                            placeholder="Şifre (isteğe bağlı)"
                                            type="password"
                                        />
                                        <button className="w-full rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.4em] text-black" type="button">
                                            Paneli aç
                                        </button>
                                    </form>
                                    <p className="mt-4 text-[0.75rem] text-white/50">
                                        Hesap açmadan katalog, fiyat ve lisans detayları tamamı açık.
                                    </p>
                                </div>
                                <div className="rounded-3xl border border-white/10 bg-[#0c0a12]/80 p-6">
                                    <div className="flex items-center justify-between text-xs uppercase tracking-[0.4em] text-white/50">
                                        <span>Admin snapshot</span>
                                        <span>Shopify sync</span>
                                    </div>
                                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                        {adminStats.map((stat) => (
                                            <div key={stat.label} className="rounded-2xl border border-white/5 bg-white/5 p-4">
                                                <p className="text-[0.7rem] uppercase tracking-[0.3em] text-white/50">{stat.label}</p>
                                                <p className="mt-2 text-2xl font-semibold">{stat.value}</p>
                                                <p className="text-xs text-emerald-300/80">{stat.delta}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="mt-6 text-[0.8rem] text-white/60">
                                        Shopify yönetim paneli arka planda açık. Drop stoğu, dijital dosyalar ve eğitim satışları aynı hesapta eşleşir.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <div className="rounded-[34px] border border-white/10 bg-black/60 p-6">
                                <div className="flex items-center justify-between text-xs uppercase tracking-[0.4em] text-white/60">
                                    <span>Seçilen ürün</span>
                                    <span>{primaryProduct.badge}</span>
                                </div>
                                <div className="relative mt-6 rounded-3xl border border-white/5 bg-gradient-to-br from-[#141016] to-[#050307] p-6">
                                    <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br from-white/5 via-transparent to-transparent" />
                                    <img src={primaryProduct.thumbnailUrl} alt={primaryProduct.name} className="relative w-full rounded-2xl border border-white/5" />
                                    <h2 className="relative mt-6 text-2xl font-semibold">{primaryProduct.name}</h2>
                                    <p className="relative mt-2 text-sm text-white/70">{primaryProduct.description}</p>
                                    <div className="relative mt-4 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/50">
                                        <span className="rounded-full border border-white/15 px-3 py-1">{primaryProduct.stock}</span>
                                        {primaryProduct.tags?.map((tag) => (
                                            <span key={tag} className="rounded-full border border-white/15 px-3 py-1">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="relative mt-6 flex items-center justify-between">
                                        <span className="text-3xl font-semibold">${primaryProduct.price}</span>
                                        <button
                                            type="button"
                                            className="rounded-full border border-white/15 px-5 py-2 text-xs uppercase tracking-[0.4em] text-white/80 transition hover:border-white/60"
                                            onClick={() => addToCart(primaryProduct)}
                                        >
                                            Sepete ekle
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-[34px] border border-white/10 bg-white/5 p-6">
                                <div className="flex items-center justify-between text-xs uppercase tracking-[0.4em] text-white/60">
                                    <span>Otomasyon akışı</span>
                                    <span>Shopify + Overlay</span>
                                </div>
                                <div className="mt-5 space-y-4">
                                    {automationRules.map((rule) => (
                                        <div key={rule.title} className="rounded-2xl border border-white/10 p-4">
                                            <div className="flex items-center justify-between text-sm text-white/70">
                                                <p className="font-medium">{rule.title}</p>
                                                <span className={rule.status === "Aktif" ? "text-emerald-300" : "text-white/40"}>{rule.status}</span>
                                            </div>
                                            <p className="mt-1 text-[0.8rem] text-white/60">{rule.detail}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="mt-16 grid gap-8 rounded-[34px] border border-white/10 bg-black/50 p-8 lg:grid-cols-[0.9fr,1.1fr]">
                        <div>
                            <div className="flex items-center justify-between text-xs uppercase tracking-[0.4em] text-white/60">
                                <span>Yeni siparişler</span>
                                <span>Gerçek zamanlı</span>
                            </div>
                            <div className="mt-8 space-y-4">
                                {workflowStages.map((stage) => (
                                    <div key={stage.label}>
                                        <div className="flex items-center justify-between text-sm text-white/70">
                                            <p>{stage.label}</p>
                                            <span>{stage.progress}%</span>
                                        </div>
                                        <div className="mt-2 h-1.5 rounded-full bg-white/10">
                                            <div className="h-full rounded-full bg-gradient-to-r from-white to-[#b28cff]" style={{ width: `${stage.progress}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
                            <div className="flex items-center justify-between text-xs uppercase tracking-[0.4em] text-white/60">
                                <span>Aktivite akışı</span>
                                <span>Audit log</span>
                            </div>
                            <div className="mt-6 space-y-4">
                                {activityLog.map((entry) => (
                                    <div key={entry.time} className="flex gap-4 rounded-2xl border border-white/10 bg-black/40 p-4">
                                        <div className="text-xs text-white/40">{entry.time}</div>
                                        <div>
                                            <p className="text-sm font-medium text-white">{entry.actor}</p>
                                            <p className="text-sm text-white/60">{entry.action}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 flex flex-wrap gap-3 text-[0.75rem] uppercase tracking-[0.4em] text-white/50">
                                <span className="rounded-full border border-white/15 px-4 py-1.5">Shopify</span>
                                <span className="rounded-full border border-white/15 px-4 py-1.5">Overlay</span>
                                <span className="rounded-full border border-white/15 px-4 py-1.5">Slack</span>
                            </div>
                        </div>
                    </section>

                    <section className="mt-16">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-[0.65rem] uppercase tracking-[0.6em] text-white/50">Koleksiyon</p>
                                <h2 className="text-3xl font-semibold">Store kartlarını overlay estetiğinde göster.</h2>
                            </div>
                            <a className="text-xs uppercase tracking-[0.4em] text-white/60" href="#">
                                Tümü →
                            </a>
                        </div>
                        <div className="mt-8 grid gap-6 md:grid-cols-2">
                            {premiumCatalog.map((product) => (
                                <article
                                    key={product.id}
                                    className="group relative flex flex-col rounded-[30px] border border-white/10 bg-gradient-to-br from-[#0b090f] to-[#050308] p-6"
                                >
                                    <div className={`pointer-events-none absolute inset-0 rounded-[30px] bg-gradient-to-br ${product.accent}`} />
                                    <div className="relative flex items-center justify-between text-xs uppercase tracking-[0.4em] text-white/50">
                                        <span>{product.badge}</span>
                                        <span>{product.stock}</span>
                                    </div>
                                    <img src={product.thumbnailUrl} alt={product.name} className="relative mt-6 h-52 w-full rounded-2xl border border-white/10 object-cover" />
                                    <h3 className="relative mt-6 text-2xl font-semibold">{product.name}</h3>
                                    <p className="relative mt-2 text-sm text-white/70">{product.description}</p>
                                    <div className="relative mt-4 flex flex-wrap gap-3 text-[0.75rem] uppercase tracking-[0.4em] text-white/50">
                                        {product.tags?.map((tag) => (
                                            <span key={tag} className="rounded-full border border-white/15 px-3 py-1">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="relative mt-6 flex items-center justify-between">
                                        <span className="text-3xl font-semibold">${product.price}</span>
                                        <button
                                            type="button"
                                            className="rounded-full border border-white/20 px-5 py-2 text-xs uppercase tracking-[0.4em] text-white/80 transition hover:border-white/60"
                                            onClick={() => addToCart(product)}
                                        >
                                            Sepete ekle
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>

                    <section className="mt-16 grid gap-8 lg:grid-cols-2">
                        <div className="rounded-[34px] border border-white/10 bg-white/5 p-8">
                            <p className="text-[0.65rem] uppercase tracking-[0.6em] text-white/60">Concierge notları</p>
                            <div className="mt-6 space-y-6">
                                {conciergeHighlights.map((item) => (
                                    <div key={item.title} className="rounded-2xl border border-white/10 bg-black/40 p-5">
                                        <p className="text-sm font-semibold">{item.title}</p>
                                        <p className="mt-2 text-sm text-white/70">{item.copy}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-[34px] border border-white/10 bg-black/60 p-8">
                            <div className="flex items-center justify-between text-xs uppercase tracking-[0.4em] text-white/60">
                                <span>Destek hattı</span>
                                <span>24/7</span>
                            </div>
                            <div className="mt-6 space-y-4">
                                {supportContacts.map((contact) => (
                                    <div key={contact.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                        <div className="flex items-center justify-between text-sm text-white/80">
                                            <p className="font-medium uppercase tracking-[0.3em]">{contact.label}</p>
                                            <span className="text-xs text-white/50">{contact.status}</span>
                                        </div>
                                        <p className="mt-2 text-base">{contact.handle}</p>
                                    </div>
                                ))}
                            </div>
                            <button className="mt-6 w-full rounded-2xl border border-white/15 px-4 py-3 text-xs uppercase tracking-[0.4em] text-white/70" type="button">
                                Slack kanalını aç
                            </button>
                        </div>
                    </section>

                    <section className="mt-16 rounded-[34px] border border-white/5 bg-gradient-to-br from-[#120d1a] to-[#050307] p-8">
                        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <p className="text-[0.65rem] uppercase tracking-[0.6em] text-[#cbb5ff]">Premium his</p>
                                <h3 className="mt-3 text-3xl font-semibold">Hızlı, detaylı ve anlaşılır bir store deneyimi.</h3>
                                <p className="mt-3 text-sm text-white/70">
                                    Overlay tasarımını tam ekran olarak gösterirken, Shopify arka planı tüm veriyi yönetilebilir tutar. Hem müşteri tarafı
                                    hem de admin tarafı aynı sayfada nefes alır.
                                </p>
                            </div>
                            <form className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto">
                                <input
                                    type="email"
                                    placeholder="Mağaza bildirimleri için e-posta"
                                    className="h-12 flex-1 rounded-full border border-white/15 bg-transparent px-4 text-sm placeholder:text-white/40 focus:border-white/60 focus:outline-none"
                                />
                                <button className="h-12 rounded-full bg-white px-6 text-xs font-semibold uppercase tracking-[0.4em] text-black" type="submit">
                                    Kaydol
                                </button>
                            </form>
                        </div>
                    </section>
                </main>
            </div>

            <footer className="border-t border-white/10 bg-black/70">
                <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-6 text-xs uppercase tracking-[0.4em] text-white/60 sm:flex-row sm:items-center sm:justify-between">
                    <span>© {currentYear} Modelerman Store</span>
                    <div className="flex gap-6">
                        <a href="/privacy" className="hover:text-white">
                            Gizlilik
                        </a>
                        <a href="/terms" className="hover:text-white">
                            Şartlar
                        </a>
                        <a href="/contact" className="hover:text-white">
                            İletişim
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default StoreLanding;
